import { useCallback, useState } from 'react'
import { FIXER_BRIEF_INTEREST } from '../../data/fixer-content.js'

export interface FixerBriefFormState {
  name: string
  company: string
  email: string
  productionType: string
  shootDates: string
  region: string
  crewSize: string
  message: string
}

const INITIAL_STATE: FixerBriefFormState = {
  name: '',
  company: '',
  email: '',
  productionType: '',
  shootDates: '',
  region: '',
  crewSize: '',
  message: '',
}

function buildBriefMessage(fields: FixerBriefFormState): string {
  const lines = [
    fields.productionType && `Production type: ${fields.productionType}`,
    fields.shootDates && `Shoot dates: ${fields.shootDates}`,
    fields.region && `Preferred region: ${fields.region}`,
    fields.crewSize && `Crew size: ${fields.crewSize}`,
    fields.message && `Additional details: ${fields.message}`,
  ].filter(Boolean)

  return lines.join('\n') || 'Norway production brief submitted from /fixer.'
}

export interface FixerBriefMessages {
  nameRequired: string
  emailRequired: string
  emailInvalid: string
}

const DEFAULT_MESSAGES: FixerBriefMessages = {
  nameRequired: 'Name is required',
  emailRequired: 'Email is required',
  emailInvalid: 'Please enter a valid email address',
}

export function useFixerBriefForm(messages: FixerBriefMessages = DEFAULT_MESSAGES) {
  const [formState, setFormState] = useState<FixerBriefFormState>(INITIAL_STATE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target
      setFormState(prev => ({ ...prev, [name]: value }))
    },
    []
  )

  const validate = useCallback(() => {
    const errors: Record<string, string> = {}
    if (!formState.name.trim()) errors.name = messages.nameRequired
    if (!formState.email.trim()) {
      errors.email = messages.emailRequired
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      errors.email = messages.emailInvalid
    }
    return errors
  }, [formState.name, formState.email, messages])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const errors = validate()
      setFieldErrors(errors)
      if (Object.keys(errors).length > 0) return

      setIsSubmitting(true)
      setSubmitStatus('idle')
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formState.name.trim(),
            company: formState.company.trim() || undefined,
            email: formState.email.trim(),
            interest: FIXER_BRIEF_INTEREST,
            message: buildBriefMessage(formState),
            source: 'fixer_brief',
          }),
        })
        if (!res.ok) throw new Error('Request failed')
        setSubmitStatus('success')
        setFormState(INITIAL_STATE)
        setFieldErrors({})
      } catch {
        setSubmitStatus('error')
      } finally {
        setIsSubmitting(false)
      }
    },
    [formState, validate]
  )

  return {
    formState,
    fieldErrors,
    isSubmitting,
    submitStatus,
    handleChange,
    handleSubmit,
  }
}
